import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Clock, Loader2, Mail, FileText, FileSpreadsheet, Upload } from 'lucide-react';
import { createElement } from 'react';
import type {
  ConductRiskDocumentType,
  ConductRiskValidationIssue,
} from '../../../api/apiTypesConductRisk';

/** I 14 document_type CR mostrati nella sezione fonti della detail page. */
export const ALL_DOC_TYPES: ConductRiskDocumentType[] = [
  'pvr_quota_a',
  'pvr_quota_a_september',
  'pvr_quota_optional',
  'pvr_quota_c_september',
  'mintr_current',
  'mintr_prior',
  'evoluzione_ricavi',
  'sei_ok',
  'icf',
  'global_advisor',
  'risultati_direttori_area',
  'performance_summary_email',
  'dettaglio_scheda_direttori_area',
  'regole_pvr',
];

export type UiSourceState =
  | 'ok'
  | 'missing_link'
  | 'parser_not_run'
  | 'parser_in_progress'
  | 'parser_failed'
  | 'parser_output_missing';

/**
 * Deriva lo stato UI aggregato a partire dalla lista di issues restituita dal
 * BE per un singolo document_type. Precedenza:
 *   failed > output_missing > in_progress > not_run > missing_link > ok
 * Nessuna issue (undefined o array vuoto) equivale a "ok".
 */
export const resolveUiState = (
  issues: ConductRiskValidationIssue[] | undefined,
): UiSourceState => {
  if (!issues || issues.length === 0) return 'ok';
  if (issues.includes('parser_failed')) return 'parser_failed';
  if (issues.includes('parser_output_missing')) return 'parser_output_missing';
  if (issues.includes('parser_in_progress')) return 'parser_in_progress';
  if (issues.includes('parser_not_run')) return 'parser_not_run';
  if (issues.includes('missing_link')) return 'missing_link';
  return 'ok';
};

export interface StateConfig {
  label: string;
  cls:   string;
  icon:  ReactNode;
}

export const STATE_CONFIG: Record<UiSourceState, StateConfig> = {
  ok: {
    label: 'Caricata',
    cls:   'bg-emerald-50 border-emerald-200 text-emerald-700',
    icon:  createElement(CheckCircle2, { className: 'w-3.5 h-3.5' }),
  },
  missing_link: {
    label: 'Da caricare',
    cls:   'bg-gray-50 border-gray-200 text-gray-600',
    icon:  createElement(Upload, { className: 'w-3.5 h-3.5' }),
  },
  parser_not_run: {
    label: 'Parsing in attesa',
    cls:   'bg-gray-50 border-gray-200 text-gray-500',
    icon:  createElement(Clock, { className: 'w-3.5 h-3.5' }),
  },
  parser_in_progress: {
    label: 'Parsing in corso',
    cls:   'bg-blue-50 border-blue-200 text-blue-700',
    icon:  createElement(Loader2, { className: 'w-3.5 h-3.5 animate-spin' }),
  },
  parser_failed: {
    label: 'Parsing fallito',
    cls:   'bg-red-50 border-red-200 text-red-700',
    icon:  createElement(AlertCircle, { className: 'w-3.5 h-3.5' }),
  },
  parser_output_missing: {
    label: 'Output non prodotto',
    cls:   'bg-red-50 border-red-200 text-red-700',
    icon:  createElement(AlertCircle, { className: 'w-3.5 h-3.5' }),
  },
};

/** Icona compatta usata per le chip delle fonti richieste dai driver. */
export const sourceIcon = (src: string): ReactNode => {
  if (src.includes('email')) return createElement(Mail, { className: 'w-2.5 h-2.5' });
  if (src.includes('pdf') || src.includes('SEI OK')) return createElement(FileText, { className: 'w-2.5 h-2.5' });
  return createElement(FileSpreadsheet, { className: 'w-2.5 h-2.5' });
};
